/**
 * SPRINT 2 — TreatmentPlanEditor
 * ==============================
 * Éditeur unique du plan de traitement : protocole principal + complémentaires
 * + note libre + SC auto (Mosteller).
 *
 * Props (contrôlé) :
 *   value            : { mainProtocol, complementaryMeds, treatmentStatus, height, weight }
 *   onChange(value)  : callback
 *   cancerType       : string (filtre catalogue)
 *   treatmentType    : string (filtre catalogue)
 *   treatmentStatuses: array<string> (depuis referenceData)
 *   therapeuticLines : array<string> (référentiel canonique, depuis referenceData)
 *   isLegacyView     : bool (bandeau orange si plan virtuel legacy)
 *
 * États internes : mode "nominal" (catalogue) ou "custom" (saisie libre).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/api";
import { AlertTriangle, Info, Edit3, BookmarkPlus, Pill, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ComplementaryMedsList from "./ComplementaryMedsList";

const ROUTES = ["IV", "PO", "SC", "IM", "Intrathecale"];

const ORAL_MOLECULE_QUICK_PICK = [
  "Capecitabine (Xeloda)",
  "Letrozole (Femara)",
  "Palbociclib (Ibrance)",
  "Ribociclib (Kisqali)",
  "Abemaciclib (Verzenios)",
  "Tamoxifene",
  "Anastrozole (Arimidex)",
  "Exemestane (Aromasin)",
  "Imatinib (Glivec)",
  "Osimertinib (Tagrisso)",
  "Erlotinib (Tarceva)",
  "Gefitinib (Iressa)",
  "Lenvatinib (Lenvima)",
  "Cabozantinib (Cabometyx)",
  "Sunitinib (Sutent)",
  "Sorafenib (Nexavar)",
];

const THERAPEUTIC_CLASS_LABELS = {
  CYTOTOXIC_CHEMO: "Chimiothérapie",
  IMMUNOTHERAPY: "Immunothérapie",
  HORMONOTHERAPY: "Hormonothérapie",
  TARGETED_THERAPY: "Thérapie ciblée",
  CDK4_6: "CDK4/6",
};

const THERAPEUTIC_CLASS_FILTER_OPTIONS = [
  { value: "ALL", label: "Toutes" },
  { value: "CYTOTOXIC_CHEMO", label: "Chimiothérapie" },
  { value: "HORMONOTHERAPY", label: "Hormonothérapie" },
  { value: "TARGETED_THERAPY", label: "Thérapie ciblée" },
  { value: "CDK4_6", label: "CDK4/6" },
  { value: "IMMUNOTHERAPY", label: "Immunothérapie" },
];

const THERAPEUTIC_CLASS_PRIORITY = ["IMMUNOTHERAPY", "CDK4_6", "TARGETED_THERAPY", "HORMONOTHERAPY", "CYTOTOXIC_CHEMO"];

// Valeur héritée absente du référentiel courant : affichable/sélectionnable, jamais une voie de création de valeur libre.
function withLegacyLine(options, current) {
  const list = Array.isArray(options) ? options : [];
  return current && !list.includes(current) ? [current, ...list] : list;
}

const MOLECULE_CLASS_HINTS = {
  letrozole: "HORMONOTHERAPY",
  letrozol: "HORMONOTHERAPY",
  anastrozole: "HORMONOTHERAPY",
  exemestane: "HORMONOTHERAPY",
  tamoxifene: "HORMONOTHERAPY",
  fulvestrant: "HORMONOTHERAPY",
  palbociclib: "CDK4_6",
  ribociclib: "CDK4_6",
  abemaciclib: "CDK4_6",
  osimertinib: "TARGETED_THERAPY",
  erlotinib: "TARGETED_THERAPY",
  gefitinib: "TARGETED_THERAPY",
  trastuzumab: "TARGETED_THERAPY",
  pertuzumab: "TARGETED_THERAPY",
  bevacizumab: "TARGETED_THERAPY",
  cetuximab: "TARGETED_THERAPY",
  panitumumab: "TARGETED_THERAPY",
  capecitabine: "CYTOTOXIC_CHEMO",
  "5 fluorouracile": "CYTOTOXIC_CHEMO",
  "5 fu": "CYTOTOXIC_CHEMO",
  oxaliplatine: "CYTOTOXIC_CHEMO",
  irinotecan: "CYTOTOXIC_CHEMO",
  irinotecane: "CYTOTOXIC_CHEMO",
  docetaxel: "CYTOTOXIC_CHEMO",
  paclitaxel: "CYTOTOXIC_CHEMO",
  carboplatine: "CYTOTOXIC_CHEMO",
  cisplatine: "CYTOTOXIC_CHEMO",
  gemcitabine: "CYTOTOXIC_CHEMO",
  cyclophosphamide: "CYTOTOXIC_CHEMO",
  doxorubicine: "CYTOTOXIC_CHEMO",
  vincristine: "CYTOTOXIC_CHEMO",
  pembrolizumab: "IMMUNOTHERAPY",
  nivolumab: "IMMUNOTHERAPY",
  atezolizumab: "IMMUNOTHERAPY",
  durvalumab: "IMMUNOTHERAPY",
  ipilimumab: "IMMUNOTHERAPY",
};

const MOLECULE_CLASS_KEYWORDS = {
  IMMUNOTHERAPY: [
    "pembrolizumab", "nivolumab", "atezolizumab", "durvalumab", "avelumab",
    "ipilimumab", "cemiplimab", "tremelimumab", "daratumumab", "elotuzumab", "isatuximab",
  ],
  CDK4_6: ["palbociclib", "ribociclib", "abemaciclib"],
  HORMONOTHERAPY: [
    "tamoxifene", "letrozole", "anastrozole", "exemestane", "fulvestrant",
    "enzalutamide", "abiraterone", "apalutamide", "darolutamide", "bicalutamide",
    "gosereline", "leuproreline", "degarelix",
  ],
  TARGETED_THERAPY: [
    "bevacizumab", "ramucirumab", "aflibercept", "lenvatinib", "cabozantinib", "axitinib",
    "sunitinib", "pazopanib", "sorafenib", "regorafenib", "erlotinib", "gefitinib",
    "osimertinib", "afatinib", "crizotinib", "alectinib", "lorlatinib", "brigatinib",
    "dabrafenib", "trametinib", "vemurafenib", "cobimetinib", "encorafenib", "binimetinib",
    "imatinib", "dasatinib", "nilotinib", "bosutinib", "ponatinib", "ibrutinib", "acalabrutinib",
    "venetoclax", "idelalisib", "ruxolitinib", "lapatinib", "tucatinib", "neratinib",
    "olaparib", "niraparib", "rucaparib", "talazoparib", "everolimus", "temsirolimus", "alpelisib",
    "trastuzumab", "pertuzumab", "cetuximab", "panitumumab", "rituximab", "obinutuzumab",
    "enfortumab", "sacituzumab", "brentuximab", "polatuzumab", "bortezomib", "carfilzomib", "ixazomib",
  ],
  CYTOTOXIC_CHEMO: [
    "cisplatine", "carboplatine", "oxaliplatine", "5 fluorouracile", "5 fu", "capecitabine",
    "gemcitabine", "pemetrexed", "methotrexate", "cytarabine", "fludarabine", "cladribine",
    "azacitidine", "decitabine", "paclitaxel", "docetaxel", "cabazitaxel", "doxorubicine",
    "epirubicine", "idarubicine", "daunorubicine", "vincristine", "vinblastine", "vinorelbine",
    "etoposide", "irinotecan", "topotecan", "cyclophosphamide", "ifosfamide", "temozolomide",
    "bendamustine", "melphalan", "chlorambucil", "busulfan", "dacarbazine", "lomustine", "carmustine",
    "bleomycine", "mitomycine",
  ],
};

function normalizeKey(value) {
  return (value || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[-_]/g, " ")
    .trim();
}

function normalizeTherapeuticClass(value) {
  if (!value) return null;
  const raw = value.toString().trim().toUpperCase().replace(/[\s-]/g, "_");
  if (THERAPEUTIC_CLASS_LABELS[raw]) return raw;
  const aliases = {
    chimiotherapie: "CYTOTOXIC_CHEMO",
    chemo: "CYTOTOXIC_CHEMO",
    immunotherapie: "IMMUNOTHERAPY",
    immuno: "IMMUNOTHERAPY",
    hormonotherapie: "HORMONOTHERAPY",
    hormono: "HORMONOTHERAPY",
    "therapie ciblee": "TARGETED_THERAPY",
    targeted: "TARGETED_THERAPY",
    association: "CYTOTOXIC_CHEMO",
  };
  return aliases[normalizeKey(value)] || null;
}

function pushClass(classes, candidate) {
  const normalized = normalizeTherapeuticClass(candidate);
  if (normalized && !classes.includes(normalized)) classes.push(normalized);
}

function getMoleculeTherapeuticClass(molecule) {
  const key = normalizeKey(molecule);
  if (!key) return null;
  if (MOLECULE_CLASS_HINTS[key]) return MOLECULE_CLASS_HINTS[key];
  for (const [classKey, keywords] of Object.entries(MOLECULE_CLASS_KEYWORDS)) {
    if (keywords.some((keyword) => key.includes(keyword))) return classKey;
  }
  return null;
}

function filterMoleculesByClass(molecules, classKey) {
  if (!classKey || classKey === "ALL") return molecules;
  return molecules.filter((molecule) => getMoleculeTherapeuticClass(molecule) === classKey);
}

function sortTherapeuticClasses(classes) {
  const ordered = THERAPEUTIC_CLASS_PRIORITY.filter((cls) => classes.includes(cls));
  ordered.push(...classes.filter((cls) => !ordered.includes(cls)));
  return ordered.slice(0, 2);
}

function deriveTherapeuticClasses(plan) {
  const classes = [];
  const mp = plan?.mainProtocol || null;
  if (mp) {
    pushClass(classes, mp.treatmentType);
    pushClass(classes, mp.label);
    pushClass(classes, mp.referenceCode);
    (mp.components || []).forEach((component) => {
      pushClass(classes, getMoleculeTherapeuticClass(component.dci));
    });
  }
  (plan?.oralTreatments || []).forEach((treatment) => {
    pushClass(classes, getMoleculeTherapeuticClass(treatment.molecule));
  });
  return sortTherapeuticClasses(classes);
}

function uniqueSortedMolecules(items) {
  return Array.from(new Set((items || []).filter(Boolean))).sort((a, b) => a.localeCompare(b));
}

function computeSC(h, w) {
  const hh = parseFloat(h);
  const ww = parseFloat(w);
  if (!hh || !ww || hh <= 0 || ww <= 0) return null;
  return Math.round(Math.sqrt((hh * ww) / 3600) * 100) / 100;
}

export default function TreatmentPlanEditor({
  value,
  onChange,
  cancerType,
  treatmentType,
  treatmentStatuses = [],
  therapeuticLines = [],
  isLegacyView = false,
  moleculesSuggestions = [],
  treatmentMode = "IV",
}) {
  const v = useMemo(() => (
    value || { mainProtocol: null, complementaryMeds: [], oralTreatments: [], therapeuticClasses: [], treatmentStatus: "En cours", height: "", weight: "" }
  ), [value]);
  const [catalog, setCatalog] = useState({ main: [], complementary: [] });
  const [mode, setMode] = useState(v.mainProtocol?.customOverride ? "custom" : "nominal");
  const [oralClassFilter, setOralClassFilter] = useState("ALL");
  const [ivClassFilter, setIvClassFilter] = useState("ALL");

  // Charge le catalogue filtré à chaque changement de cancerType
  useEffect(() => {
    if (!cancerType) return;
    const loadMain = api.get(`/reference-protocols?kind=MAIN&cancerType=${encodeURIComponent(cancerType)}`);
    Promise.all([
      loadMain.catch(() => ({ data: { items: [] } })),
      api.get(`/reference-protocols?kind=COMPLEMENTARY`).catch(() => ({ data: { items: [] } })),
    ])
      .then(([mainRes, cplRes]) => {
        setCatalog({ main: mainRes.data?.items || [], complementary: cplRes.data?.items || [] });
      });
  }, [cancerType]);

  const sc = useMemo(() => computeSC(v.height, v.weight), [v.height, v.weight]);
  const inferredTherapeuticClasses = useMemo(() => deriveTherapeuticClasses(v), [v]);
  const inferredClassKey = inferredTherapeuticClasses.join("|");
  const currentClassKey = (v.therapeuticClasses || []).join("|");
  const moleculeLibrary = useMemo(
    () => uniqueSortedMolecules([...ORAL_MOLECULE_QUICK_PICK, ...(moleculesSuggestions || [])]),
    [moleculesSuggestions]
  );
  const oralMoleculeLibrary = useMemo(
    () => filterMoleculesByClass(moleculeLibrary, oralClassFilter),
    [moleculeLibrary, oralClassFilter]
  );
  const ivMoleculeLibrary = useMemo(
    () => filterMoleculesByClass(moleculeLibrary, ivClassFilter),
    [moleculeLibrary, ivClassFilter]
  );
  const oralQuickPickFiltered = useMemo(
    () => filterMoleculesByClass(ORAL_MOLECULE_QUICK_PICK, oralClassFilter),
    [oralClassFilter]
  );

  useEffect(() => {
    if (inferredClassKey !== currentClassKey) {
      onChange({ ...v, therapeuticClasses: inferredTherapeuticClasses });
    }
  }, [currentClassKey, inferredClassKey, inferredTherapeuticClasses, onChange, v]);

  const updateMain = (patch) => {
    onChange({ ...v, mainProtocol: { ...(v.mainProtocol || {}), ...patch } });
  };

  const updateOralTreatment = (i, patch) => {
    const items = [...(v.oralTreatments || [])];
    items[i] = { ...items[i], ...patch };
    onChange({ ...v, oralTreatments: items });
  };

  const addOralTreatment = () => {
    const today = new Date().toISOString().slice(0, 10);
    onChange({
      ...v,
      oralTreatments: [
        ...(v.oralTreatments || []),
        { molecule: "", frequency: "daily", startDate: today, nextDoseDate: today, status: "active" },
      ],
    });
  };

  const removeOralTreatment = (i) => {
    onChange({ ...v, oralTreatments: (v.oralTreatments || []).filter((_, idx) => idx !== i) });
  };

  // Sélection d'un protocole du catalogue -> snapshot des composants
  const selectFromCatalog = (code) => {
    const ref = catalog.main.find((m) => m.code === code);
    if (!ref) return;
    onChange({
      ...v,
      mainProtocol: {
        referenceCode: ref.code,
        label: ref.label,
        kind: "MAIN",
        cancerType: ref.cancerType || cancerType,
        treatmentType: ref.treatmentType || null,
        // Pré-remplissage uniquement si le catalogue ne couvre qu'une seule ligne et n'impose pas un choix explicite
        // (protocoles à usages multiples, `explicitLineSelection`) ; sinon le médecin sélectionne la ligne (2026-09-16)
        therapeuticLine: (ref.therapeuticLines || []).length === 1 && !ref.explicitLineSelection ? ref.therapeuticLines[0] : "",
        cycle: ref.defaultCycle || null,
        administrationRoute: ref.components?.[0]?.route || "IV",
        customOverride: false,
        startedAt: v.mainProtocol?.startedAt || null,
        components: (ref.components || []).map((c) => ({
          dci: c.dci,
          doseFormula: c.doseFormula || null,
          route: c.route || null,
          days: c.days || null,
          biosimilarChosen: null,
        })),
        freeNote: v.mainProtocol?.freeNote || null,
        _biosimilarsRef: ref.biosimilarsMA || {}, // côté UI seulement
      },
    });
    setMode("nominal");
  };

  const clearMain = () => {
    onChange({ ...v, mainProtocol: null });
    setMode("nominal");
  };

  const enterCustom = () => {
    onChange({
      ...v,
      mainProtocol: {
        referenceCode: v.mainProtocol?.referenceCode || "CUSTOM-DRAFT",
        label: v.mainProtocol?.label || "",
        kind: "MAIN",
        cancerType: cancerType || null,
        treatmentType: null,
        therapeuticLine: v.mainProtocol?.therapeuticLine || "",
        cycle: v.mainProtocol?.cycle || { lengthDays: null, frequency: "" },
        administrationRoute: v.mainProtocol?.administrationRoute || "IV",
        customOverride: true,
        components: v.mainProtocol?.components?.length ? v.mainProtocol.components : [
          { dci: "", doseFormula: "", route: "IV", days: "", biosimilarChosen: null },
        ],
        freeNote: v.mainProtocol?.freeNote || null,
      },
    });
    setMode("custom");
  };

  const updateCustomComponent = (i, patch) => {
    const comps = [...(v.mainProtocol?.components || [])];
    comps[i] = { ...comps[i], ...patch };
    updateMain({ components: comps });
  };

  const addCustomComponent = () => {
    const comps = [...(v.mainProtocol?.components || []), { dci: "", doseFormula: "", route: "IV", days: "", biosimilarChosen: null }];
    updateMain({ components: comps });
  };

  const removeCustomComponent = (i) => {
    const comps = (v.mainProtocol?.components || []).filter((_, idx) => idx !== i);
    updateMain({ components: comps });
  };

  const updateBiosimilar = (componentIdx, biosimilar) => {
    const comps = [...(v.mainProtocol?.components || [])];
    comps[componentIdx] = { ...comps[componentIdx], biosimilarChosen: biosimilar || null };
    updateMain({ components: comps });
  };

  const mp = v.mainProtocol;
  const biosimilarsRef = mp?._biosimilarsRef || {};
  const hasOralTreatments = (v.oralTreatments || []).length > 0;
  const shouldShowOralBlock = treatmentMode === "Oral" || treatmentMode === "Mixte" || hasOralTreatments;
  const oralBlockTitle = treatmentMode === "Oral" ? "Traitement per os à domicile" : "Traitement per os associé";

  const oralTreatmentsBlock = shouldShowOralBlock ? (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-4 space-y-3" data-testid="oral-treatments-block">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
          <Pill className="w-4 h-4 text-emerald-700" /> {oralBlockTitle}
        </h3>
        <button
          type="button"
          onClick={addOralTreatment}
          className="text-xs text-emerald-700 hover:text-emerald-900 font-semibold"
          data-testid="oral-treatment-add-button"
        >
          + Ajouter
        </button>
      </div>
      <div className="max-w-sm" data-testid="oral-treatment-class-filter-wrapper">
        <label className="text-xs text-slate-600">Filtrer la bibliothèque par classe</label>
        <select
          value={oralClassFilter}
          onChange={(e) => setOralClassFilter(e.target.value)}
          className="mt-1 w-full text-sm border border-emerald-200 bg-white rounded px-2 py-1.5"
          data-testid="oral-treatment-class-filter"
        >
          {THERAPEUTIC_CLASS_FILTER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
      {(v.oralTreatments || []).length === 0 ? (
        <p className="text-xs text-slate-500" data-testid="oral-treatment-empty-state">
          Aucun traitement oral à domicile configuré.
        </p>
      ) : (
        <div className="space-y-2">
          {(v.oralTreatments || []).map((t, i) => (
            <div key={t.id || i} className="grid grid-cols-1 md:grid-cols-12 gap-2 bg-white border border-emerald-100 rounded-lg p-3" data-testid={`oral-treatment-row-${i}`}>
              <div className="md:col-span-3">
                <label className="text-xs text-slate-600">Molécule</label>
                <input
                  type="text"
                  value={t.molecule || ""}
                  onChange={(e) => updateOralTreatment(i, { molecule: e.target.value })}
                  list="oral-molecule-suggestions"
                  placeholder="Choisir ou saisir une DCI"
                  className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                  data-testid={`oral-treatment-molecule-${i}`}
                />
                <div className="mt-1 flex flex-wrap gap-1" data-testid={`oral-treatment-quick-picks-${i}`}>
                  {oralQuickPickFiltered.slice(0, 6).map((molecule) => (
                    <button
                      key={molecule}
                      type="button"
                      onClick={() => updateOralTreatment(i, { molecule })}
                      className="rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 hover:bg-emerald-100 transition-colors"
                      data-testid={`oral-treatment-quick-pick-${i}-${normalizeKey(molecule).replace(/\s+/g, '-')}`}
                    >
                      {molecule.split(" (")[0]}
                    </button>
                  ))}
                </div>
                {oralClassFilter !== "ALL" && oralMoleculeLibrary.length === 0 && (
                  <p className="mt-1 text-[10px] font-semibold text-amber-700" data-testid={`oral-treatment-filter-empty-${i}`}>
                    Aucune molécule classée dans ce filtre. Vous pouvez saisir une DCI manuellement.
                  </p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-slate-600">Fréquence</label>
                <select
                  value={t.frequency || "daily"}
                  onChange={(e) => updateOralTreatment(i, { frequency: e.target.value })}
                  className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                  data-testid={`oral-treatment-frequency-${i}`}
                >
                  <option value="daily">Quotidien</option>
                  <option value="every_other_day">Un jour sur deux</option>
                  <option value="weekly">Hebdomadaire</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-slate-600">Date début</label>
                <input
                  type="date"
                  value={t.startDate || ""}
                  onChange={(e) => updateOralTreatment(i, { startDate: e.target.value })}
                  className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                  data-testid={`oral-treatment-start-date-${i}`}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-slate-600">Prochaine prise</label>
                <input
                  type="date"
                  value={t.nextDoseDate || ""}
                  onChange={(e) => updateOralTreatment(i, { nextDoseDate: e.target.value })}
                  className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                  data-testid={`oral-treatment-next-dose-date-${i}`}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-slate-600">Statut</label>
                <select
                  value={t.status || "active"}
                  onChange={(e) => updateOralTreatment(i, { status: e.target.value })}
                  className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                  data-testid={`oral-treatment-status-${i}`}
                >
                  <option value="active">Actif</option>
                  <option value="suspended">Suspendu</option>
                  <option value="stopped">Arrêté</option>
                </select>
              </div>
              <button
                type="button"
                onClick={() => removeOralTreatment(i)}
                className="md:col-span-1 text-slate-400 hover:text-red-600 text-sm self-end py-1.5"
                data-testid={`oral-treatment-remove-${i}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="space-y-4" data-testid="treatment-plan-editor">
      <datalist id="dci-suggestions" data-testid="dci-suggestions-library">
        {ivMoleculeLibrary.map((m) => <option key={`dci-${m}`} value={m} />)}
      </datalist>
      <datalist id="oral-molecule-suggestions" data-testid="oral-molecule-suggestions-library">
        {oralMoleculeLibrary.map((m) => <option key={`oral-${m}`} value={m} />)}
      </datalist>
      {isLegacyView && (
        <div className="rounded-md border border-orange-300 bg-orange-50 px-3 py-2 text-sm text-orange-800" data-testid="editor-legacy-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Plan au format historique.</strong> Sélectionnez un protocole du catalogue
              ou passez en mode personnalisé pour formaliser le plan.
              Les anciennes molécules restent conservées à titre d&rsquo;archive.
            </div>
          </div>
        </div>
      )}

      {!cancerType ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-600 text-center" data-testid="editor-context-missing">
          <Info className="w-4 h-4 inline mr-1" />
          Renseignez d&rsquo;abord le type de cancer.
        </div>
      ) : (
        <>
          {treatmentMode === "Oral" && oralTreatmentsBlock}

          {/* Bloc A — Protocole principal */}
          <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="main-protocol-block">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800 text-sm">Protocole principal</h3>
              {mode === "custom" && (
                <button
                  type="button"
                  onClick={() => setMode("nominal")}
                  className="text-xs text-blue-600 hover:text-blue-800"
                  data-testid="back-to-catalog-btn"
                >
                  ← Revenir au catalogue
                </button>
              )}
            </div>

            {mode === "nominal" ? (
              <>
                <select
                  value={mp?.referenceCode || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "__NONE__") clearMain();
                    else if (val === "__CUSTOM__") enterCustom();
                    else if (val) selectFromCatalog(val);
                  }}
                  className="w-full text-sm border border-slate-300 rounded px-2 py-2"
                  data-testid="main-protocol-select"
                >
                  <option value="">— Sélectionner un protocole —</option>
                  {catalog.main.map((p) => (
                    <option key={p.code} value={p.code}>{p.label}</option>
                  ))}
                  <option disabled>──────────</option>
                  <option value="__NONE__">Aucun protocole principal (surveillance)</option>
                  <option value="__CUSTOM__">Saisir un protocole personnalisé…</option>
                </select>

                {catalog.main.length === 0 && (
                <p className="text-xs text-slate-500" data-testid="main-protocol-empty-state">
                    Aucun protocole catalogue pour ce contexte. Passez en mode personnalisé.
                  </p>
                )}

                {mp && mp.referenceCode !== "CUSTOM-DRAFT" && (
                  <div className="space-y-2 border-t border-slate-100 pt-3">
                    {mp.components?.map((c, i) => {
                      const bios = biosimilarsRef[c.dci] || [];
                      return (
                        <div key={i} className="flex flex-wrap items-baseline gap-x-2 text-sm" data-testid={`component-row-${i}`}>
                          <span className="font-medium text-slate-800">{c.dci}</span>
                          {c.doseFormula && <span className="text-slate-500">{c.doseFormula}</span>}
                          {c.route && <span className="text-slate-400 text-xs">{c.route}</span>}
                          {c.days && <span className="text-slate-400 text-xs">· {c.days}</span>}
                          {bios.length > 0 && (
                            <select
                              value={c.biosimilarChosen || ""}
                              onChange={(e) => updateBiosimilar(i, e.target.value)}
                              className="text-xs border border-slate-200 rounded px-1.5 py-0.5 ml-auto"
                              data-testid={`biosimilar-select-${i}`}
                            >
                              <option value="">{c.dci}</option>
                              {bios.map((b) => <option key={b} value={b}>{b}</option>)}
                            </select>
                          )}
                        </div>
                      );
                    })}
                    {/* Ligne thérapeutique + cycle info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2 text-xs">
                      <div>
                        <label className="text-slate-500">Ligne thérapeutique</label>
                        <select
                          value={mp.therapeuticLine || ""}
                          onChange={(e) => updateMain({ therapeuticLine: e.target.value || null })}
                          className="w-full border border-slate-200 rounded px-2 py-1 mt-0.5 bg-white"
                          data-testid="main-therapeutic-line-input"
                        >
                          <option value="">Sélectionner...</option>
                          {withLegacyLine(therapeuticLines, mp.therapeuticLine).map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-slate-500">Démarré le</label>
                        <input
                          type="date"
                          value={mp.startedAt || ""}
                          onChange={(e) => updateMain({ startedAt: e.target.value })}
                          className="w-full border border-slate-200 rounded px-2 py-1 mt-0.5"
                          data-testid="main-started-at-input"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Mode CUSTOM */
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-slate-600">Libellé du protocole *</label>
                  <input
                    type="text"
                    value={mp?.label || ""}
                    onChange={(e) => updateMain({ label: e.target.value })}
                    className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                    placeholder="ex. R-CHOP modifié"
                    data-testid="custom-label-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                    <label className="text-xs text-slate-600">Composants</label>
                    <div className="w-full md:w-72" data-testid="custom-component-class-filter-wrapper">
                      <label className="text-xs text-slate-600">Filtrer la bibliothèque par classe</label>
                      <select
                        value={ivClassFilter}
                        onChange={(e) => setIvClassFilter(e.target.value)}
                        className="mt-1 w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                        data-testid="custom-component-class-filter"
                      >
                        {THERAPEUTIC_CLASS_FILTER_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {(mp?.components || []).map((c, i) => (
                    <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`custom-component-row-${i}`}>
                      <input
                        type="text"
                        placeholder="DCI"
                        value={c.dci}
                        onChange={(e) => updateCustomComponent(i, { dci: e.target.value })}
                        list="dci-suggestions"
                        className="col-span-4 text-sm border border-slate-200 rounded px-2 py-1"
                        data-testid={`custom-component-dci-${i}`}
                      />
                      <input type="text" placeholder="Dose" value={c.doseFormula || ""} onChange={(e) => updateCustomComponent(i, { doseFormula: e.target.value })} className="col-span-3 text-sm border border-slate-200 rounded px-2 py-1" data-testid={`custom-component-dose-${i}`} />
                      <select value={c.route || "IV"} onChange={(e) => updateCustomComponent(i, { route: e.target.value })} className="col-span-2 text-sm border border-slate-200 rounded px-2 py-1" data-testid={`custom-component-route-${i}`}>
                        {ROUTES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <input type="text" placeholder="Jours" value={c.days || ""} onChange={(e) => updateCustomComponent(i, { days: e.target.value })} className="col-span-2 text-sm border border-slate-200 rounded px-2 py-1" data-testid={`custom-component-days-${i}`} />
                      <button type="button" onClick={() => removeCustomComponent(i)} className="col-span-1 text-slate-400 hover:text-red-600 text-xs" data-testid={`custom-component-remove-${i}`}>✕</button>
                    </div>
                  ))}
                  <button type="button" onClick={addCustomComponent} className="text-xs text-blue-600 hover:text-blue-800" data-testid="custom-component-add-btn">
                    + Ajouter un composant
                  </button>
                </div>
                {/* Sprint 6.5 — Sauvegarder ce protocole custom dans le catalogue clinique */}
                {mp?.label && (mp?.components || []).some(c => c.dci?.trim()) && (
                  <SaveCustomProtocolButton
                    label={mp.label}
                    components={mp.components}
                    therapeuticLine={mp.therapeuticLine}
                    cancerType={mp.cancerType}
                    cycle={mp.cycle}
                    onSaved={(newCode) => {
                      // Rebascule en mode nominal avec le nouveau code
                      updateMain({ referenceCode: newCode, customOverride: false });
                      setMode("nominal");
                    }}
                  />
                )}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <label className="text-slate-500">Ligne thérapeutique</label>
                    <select value={mp?.therapeuticLine || ""} onChange={(e) => updateMain({ therapeuticLine: e.target.value || null })} className="w-full border border-slate-200 rounded px-2 py-1 bg-white" data-testid="custom-therapeutic-line-input">
                      <option value="">Sélectionner...</option>
                      {withLegacyLine(therapeuticLines, mp?.therapeuticLine).map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-slate-500">Fréquence</label>
                    <input type="text" value={mp?.cycle?.frequency || ""} onChange={(e) => updateMain({ cycle: { ...(mp?.cycle || {}), frequency: e.target.value } })} className="w-full border border-slate-200 rounded px-2 py-1" data-testid="custom-frequency-input" />
                  </div>
                </div>
              </div>
            )}

            {/* FreeNote — compact */}
            {mp && (
              <div className="pt-2 border-t border-slate-100">
                <label className="text-xs text-slate-500 flex items-center gap-1">
                  <Edit3 className="w-3 h-3" /> Précision / adaptation clinique (optionnel)
                </label>
                <input
                  type="text"
                  value={mp.freeNote || ""}
                  onChange={(e) => updateMain({ freeNote: e.target.value.slice(0, 500) })}
                  placeholder="ex. Vincristine réduite à 1 mg — neuropathie G1"
                  className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 mt-1"
                  maxLength={500}
                  data-testid="main-free-note-input"
                />
                {mp.freeNote && (
                  <div className="text-[10px] text-slate-400 text-right">{mp.freeNote.length}/500</div>
                )}
              </div>
            )}
          </div>

          {treatmentMode !== "Oral" && oralTreatmentsBlock}

          {/* Bloc B — Complémentaires */}
          <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="complementary-block">
            <h3 className="font-semibold text-slate-800 text-sm">Médicaments complémentaires</h3>
            <ComplementaryMedsList
              items={v.complementaryMeds || []}
              onChange={(items) => onChange({ ...v, complementaryMeds: items })}
              catalog={catalog.complementary}
            />
          </div>

          {/* Bloc B3 — Classe thérapeutique déduite pour check-in contextualisé */}
          <div className="rounded-lg border border-teal-200 bg-teal-50/30 p-4 space-y-3" data-testid="therapeutic-class-auto-block">
            <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-teal-700" /> Classe thérapeutique déduite
            </h3>
            {(v.therapeuticClasses || []).length > 0 ? (
              <div className="flex flex-wrap gap-2" data-testid="therapeutic-class-auto-list">
                {(v.therapeuticClasses || []).map((cls, idx) => (
                  <span
                    key={cls}
                    className="inline-flex items-center rounded-full border border-teal-200 bg-white px-2.5 py-1 text-xs font-semibold text-teal-800"
                    data-testid={`therapeutic-class-auto-badge-${idx}`}
                  >
                    {THERAPEUTIC_CLASS_LABELS[cls] || cls}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-500" data-testid="therapeutic-class-auto-empty">
                Sera déduite automatiquement dès qu&rsquo;un protocole ou un traitement oral est renseigné.
              </p>
            )}
            <p className="text-xs text-slate-500">Alimente les check-ins contextualisés sans saisie manuelle supplémentaire.</p>
          </div>

          {/* Bloc C — SC + status */}
          <div className="rounded-lg border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="sc-status-block">
            <div>
              <label className="text-xs text-slate-600">Taille (cm)</label>
              <input
                type="number"
                value={v.height || ""}
                onChange={(e) => onChange({ ...v, height: e.target.value })}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                data-testid="height-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">Poids (kg)</label>
              <input
                type="number"
                value={v.weight || ""}
                onChange={(e) => onChange({ ...v, weight: e.target.value })}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                data-testid="weight-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">Surface corporelle (Mosteller)</label>
              <div className="w-full text-sm border border-slate-200 bg-slate-50 rounded px-2 py-1.5" data-testid="sc-display">
                {sc != null ? `${sc} m²` : <span className="text-slate-400 italic">non calculable</span>}
              </div>
            </div>
            <div className="md:col-span-3">
              <label className="text-xs text-slate-600">Statut du traitement</label>
              <select
                value={v.treatmentStatus || "En cours"}
                onChange={(e) => onChange({ ...v, treatmentStatus: e.target.value })}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                data-testid="treatment-status-select-plan"
              >
                {withLegacyLine(treatmentStatuses, v.treatmentStatus).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Sprint 6.5 — Bouton "Enregistrer ce protocole dans ma clinique"
 * Appelle POST /api/reference-protocols pour créer un protocole MAIN clinique-scoped.
 * Le MEDECIN a le droit depuis 6.5 (via MEDECIN_CREATE_ROLES côté backend).
 */
function SaveCustomProtocolButton({ label, components, therapeuticLine, cancerType, cycle, onSaved }) {
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!window.confirm(
      `Enregistrer "${label}" dans le catalogue de votre clinique ?\n\n` +
      `Il sera réutilisable par tous les médecins et infirmières de votre clinique.`
    )) return;
    setSaving(true);
    try {
      const validComps = (components || [])
        .filter(c => c.dci?.trim())
        .map(c => ({
          dci: c.dci.trim(),
          doseFormula: c.doseFormula || "",
          route: c.route || "IV",
          days: c.days || "",
        }));
      if (validComps.length === 0) {
        toast.error("Ajoutez au moins un composant DCI valide");
        return;
      }
      const { data } = await api.post("/reference-protocols", {
        label: label.trim(),
        kind: "MAIN",
        cancerType: cancerType || null,
        category: null,
        therapeuticLines: therapeuticLine ? [therapeuticLine] : [],
        components: validComps,
        defaultCycle: cycle?.lengthDays || cycle?.frequency ? {
          lengthDays: cycle.lengthDays || null,
          frequency: cycle.frequency || "",
        } : null,
        biosimilarsMA: {},
      });
      toast.success(`Protocole "${label}" enregistré dans votre clinique`, { duration: 5000 });
      onSaved?.(data.code);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "DUPLICATE_PROTOCOL_CODE") {
        toast.error("Un protocole avec ce code existe déjà. Modifiez le libellé.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur lors de l'enregistrement");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleSave}
      disabled={saving}
      className="w-full mt-2 px-3 py-2 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50"
      data-testid="save-custom-protocol-btn"
    >
      <BookmarkPlus className="w-4 h-4" />
      {saving ? "Enregistrement…" : "Enregistrer ce protocole dans ma clinique"}
    </button>
  );
}
